import React, { FC, useCallback, useEffect, useState } from 'react';
import { Redirect, RouteComponentProps, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';
import { Raffle, RaffleUpsert, ValidCriterions } from '../../../../../shared/orders/raffle';
import { ActionButton, InlineHelp, PageHeader, UserStateComponent } from '../../../../components';
import { MaterialIconText } from '../../../../components/MaterialIconText';
import { JSONForm, PreSubmitData } from '../../../../components/json-form/JSONForm';
import { useTranslation } from '../../../../translations';
import { classNames, Fetcher, useFetcher } from '../../../../utils';
import { RaffleTextEdit } from './RaffleTextEdit';
import { ProductSelector } from './productSelector';
import { RaffleEntrants, raffleEntrantsRef } from './raffleEntrants';
import { RoleSelector } from './roleSelector';
import { RunRaffleComponent } from './runRaffle';
import { WeightEditor } from './weightEditor';

import './style.scss';

export const RaffleEditorWrapper: FC<RouteComponentProps<{ id?: string }>> = ({ match }) => {
  const id = match.params.id ? Number.parseInt(match.params.id, 10) : undefined;
  const fetcher = useFetcher(async () => (id ? await api.getFullRaffleById(id) : undefined), [id]);
  if (!id) {
    return <RaffleEditor />;
  }

  if (fetcher.error) {
    return <Redirect to="/housekeeping/gatekeeping" />;
  }

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  return <RaffleEditor raffle={fetcher.data} refresh={fetcher.refresh} />;
};

interface Props {
  readonly raffle?: Raffle;
  readonly refresh?: () => void;
}

const RaffleEditor: FC<Props> = ({ raffle, refresh }) => {
  const history = useHistory();
  const [raffleTree, setRaffleTree] = useState<ValidCriterions>();
  const [roleIds, setRoleIds] = useState<number[]>(raffle?.rolesOverride ?? []);
  const [productIds, setProductIds] = useState<number[]>(
    raffle?.products.map(({ id }) => id) ?? [],
  );

  const [raffleStatus, setRaffleStatus] = useState<'closed' | 'drawn' | 'open'>(
    raffle?.status ?? 'open',
  );

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('2');
  const [deleteModal, setDeleteModal] = useState(false);

  const refreshRaffle = useCallback(() => {
    refresh!();
    if (raffleEntrantsRef.current) {
      raffleEntrantsRef.current();
    }
  }, [refresh]);

  const preSubmit = useCallback(
    (opts: PreSubmitData<RaffleUpsert>) => {
      opts.inputs.criterion = raffleTree!;
      opts.inputs.productIds = productIds;
      opts.inputs.rolesOverride = roleIds;
    },
    [raffleTree, productIds, roleIds],
  );

  const isEdit = !!raffle;

  const { ts } = useTranslation();

  const onSuccess = useCallback(
    (data?: { id?: number }) => {
      toast.success(`${ts(isEdit ? 'silo.successfully.updated' : 'silo.successfully.created')}!`);

      setHasUnsavedChanges(false);
      if (data?.id) {
        history.push(`/housekeeping/gatekeeping/${data.id}`);
      } else {
        refreshRaffle();
      }
    },
    [history, isEdit, refreshRaffle, ts],
  );

  const onTreeUpdate = useCallback((newRaffleTree: ValidCriterions) => {
    setRaffleTree(newRaffleTree);
    setHasUnsavedChanges(true);
  }, []);

  useEffect(() => {
    if (!raffle) {
      return;
    }

    setRaffleTree(raffle.criterion);
  }, [raffle]);

  return (
    <UserStateComponent>
      {deleteModal && (
        <DeleteRaffleModal
          close={() => {
            setDeleteModal(false);
          }}
          raffleId={raffle!.id}
        />
      )}
      <PageHeader>
        {isEdit ? `${ts('silo.editing')} ${raffle.name}` : ts('silo.creating')}
      </PageHeader>

      <Row>
        <Col lg={4} xs={12}>
          <JSONForm<{ id?: number }, RaffleUpsert>
            method={isEdit ? 'patch' : 'post'}
            onSuccess={onSuccess}
            path={isEdit ? `/api/raffles/${raffle.id}` : '/api/raffles'}
            preSubmit={preSubmit}
          >
            <Card className="mb-3">
              <CardHeader>
                {ts('silo.information')} {raffleStatusBadge(raffleStatus)}
              </CardHeader>
              <CardBody>
                <Row>
                  <Col lg={10} xs={12}>
                    <FormGroup>
                      <Label for="name">{ts('name')}</Label>
                      <Input defaultValue={raffle?.name} id="name" name="name" required />
                    </FormGroup>
                  </Col>
                  <Col lg={10} xs={12}>
                    <FormGroup check className="mt-10">
                      <Input
                        defaultChecked={raffleStatus === 'open'}
                        id="openRaffle"
                        onChange={(e) => {
                          setRaffleStatus(e.currentTarget.checked ? 'open' : 'closed');
                        }}
                        type="checkbox"
                      />
                      <Label check for="openRaffle">
                        {ts('raffles.editor.allowNewEntriesToTheSilo')}
                      </Label>
                      <input name="status" type="hidden" value={raffleStatus} />
                    </FormGroup>
                  </Col>
                  <Col lg={10} xs={12}>
                    <FormGroup check className="mt-10">
                      <Input
                        defaultChecked={raffle?.overflowEntries ?? false}
                        id="overflowEntries"
                        name="overflowEntries"
                        type="checkbox"
                      />
                      <Label check for="overflowEntries">
                        {ts('raffles.editor.overflowStockSilo')}
                        <InlineHelp>{ts('raffles.editor.overflowStockSilo.help')}</InlineHelp>
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <Row>
                  <Col lg={4} xs={12}>
                    <Button block color="primary" id="submitForm">
                      {ts(isEdit ? 'common.update' : 'common.create')} {ts('silo')}
                    </Button>
                  </Col>
                  {isEdit && (
                    <Col lg={4} xs={12}>
                      <Button
                        block
                        color="danger"
                        id="openDeleteRaffle"
                        onClick={() => {
                          setDeleteModal(true);
                        }}
                      >
                        {ts('delete_silo')}
                      </Button>
                    </Col>
                  )}
                </Row>
              </CardFooter>
            </Card>
            <Card className="mb-3">
              <ProductSelector defaultProducts={raffle?.products} onChange={setProductIds} />
            </Card>
            <Card className="mb-3">
              <RoleSelector selectedIds={roleIds} setSelectedIds={setRoleIds} />
            </Card>
          </JSONForm>
        </Col>
        <Col lg={7} xs={12}>
          {hasUnsavedChanges && (
            <Alert className="mb-3 raffle-weight-unsaved" color="warning">
              <MaterialIconText name="save">
                {ts('you_currently_have_unsaved_silo_weight_changes')}
              </MaterialIconText>
            </Alert>
          )}
          {isEdit && (
            <Nav tabs>
              <NavItem
                id="raffleTabEntrants"
                onClick={() => {
                  setActiveTab('2');
                }}
              >
                <div className={classNames({ active: activeTab === '2' }, 'nav-link')}>
                  {ts('entrants')}
                </div>
              </NavItem>
              <NavItem
                id="raffleTabText"
                onClick={() => {
                  setActiveTab('3');
                }}
              >
                <div className={classNames({ active: activeTab === '3' }, 'nav-link')}>
                  {ts('wording')}
                </div>
              </NavItem>
              <NavItem
                id="raffleTabRun"
                onClick={() => {
                  setActiveTab('4');
                }}
              >
                <div className={classNames({ active: activeTab === '4' }, 'nav-link')}>
                  {ts('run')}
                </div>
              </NavItem>
              <NavItem
                id="raffleTabWeight"
                onClick={() => {
                  setActiveTab('1');
                }}
              >
                <div className={classNames({ active: activeTab === '1' }, 'nav-link')}>
                  {ts('weight_configuration')}
                </div>
              </NavItem>
            </Nav>
          )}
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <WeightEditor onUpdate={onTreeUpdate} raffleTree={raffleTree} />
            </TabPane>
            {isEdit && (
              <TabPane tabId="2">
                <RaffleEntrants raffle={raffle} />
              </TabPane>
            )}
            {isEdit && (
              <TabPane tabId="3">
                <RaffleTextEdit raffle={raffle} />
              </TabPane>
            )}
            {isEdit && (
              <TabPane tabId="4">
                <RunRaffleComponent raffle={raffle} />
              </TabPane>
            )}
          </TabContent>
        </Col>
      </Row>
    </UserStateComponent>
  );
};

function raffleStatusBadge(status: 'closed' | 'drawn' | 'open'): React.JSX.Element {
  let statusText = 'Open to Entries';
  let statusColor = 'success';

  if (status === 'closed') {
    statusText = 'Closed to Entries';
    statusColor = 'secondary';
  } else if (status === 'drawn') {
    statusText = 'Recently Drawn';
    statusColor = 'info';
  }

  return <Badge color={statusColor}>{statusText}</Badge>;
}

const DeleteRaffleModal: FC<{ readonly raffleId: number; readonly close: () => void }> = ({
  raffleId,
  close,
}) => {
  const { ts } = useTranslation();
  const history = useHistory();
  return (
    <Modal isOpen>
      <ModalHeader>{ts('are_you_sure')}</ModalHeader>
      <ModalBody>
        <p>{ts('are_you_sure_you_want_to_delete_silo')}</p>
        <p>{ts('all_ticket_data_will_be_lost')}</p>
      </ModalBody>
      <ModalFooter>
        <ActionButton
          action={async () => {
            await api.deleteRaffle(raffleId);
          }}
          color="danger"
          id="deleteRaffle"
          onSuccess={() => {
            close();
            toast.success(ts('successfully_deleted_silo'));
            history.push('/housekeeping/gatekeeping');
          }}
        >
          {ts('delete_silo')}
        </ActionButton>
        <Button
          color="secondary"
          onClick={() => {
            close();
          }}
        >
          {ts('common.cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
